import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import Paper from '@mui/material/Paper';
import { Task } from '@repo/shared/domain';
import { Spinner } from '@repo/components';
import { DataGrid, DataGridColumn } from '@repo/components';
import { Badge } from '@repo/components';
import { sortToString, stringToSort } from '@repo/shared/helpers';
import { constructQuery } from '@repo/shared/helpers';
import useUpdateQueryParams from 'src/hooks/useUpdateQueryParams';
import { getPartnerCompanyTasks } from 'src/api/partner';
import { useBreadcrumbLevel } from '@repo/components/hooks';

type TaskSearchSortOptions = 'validTo' | 'updatedAt' | 'createdAt' | 'name';
type SortDirection = 'asc' | 'desc';
type TaskSearchSort = `${TaskSearchSortOptions}:${SortDirection}`;

type TaskSearch = {
	sort: TaskSearchSort;
	page?: number;
};

export const Route = createFileRoute('/partner_/$partnerId/tasks/')({
	component: RouteComponent,
	validateSearch: (search: Record<string, unknown>): TaskSearch => {
		// validate and parse the search params into a typed state
		return {
			sort: (typeof search.sort === 'string' ? (search.sort as TaskSearchSort) : undefined) || 'updatedAt:desc',
			page: (search.page as number | undefined) || 0,
		};
	},
});

const variants = {
	done: 'success',
	pending: 'outline',
	todo: 'default',
	expiryWarning: 'warning',
	expired: 'error',
	failed: 'error',
} as const;

const states = {
	done: 'Done',
	pending: 'Pending',
	todo: 'Todo',
	expiryWarning: 'Almost Expiring',
	expired: 'Expired',
	failed: 'Failed',
	superseded: 'Superseded',
};

function RouteComponent() {
	const { partnerId } = Route.useParams();
	useBreadcrumbLevel(1, 'Tasks');
	const navigate = useNavigate();
	const { sort, page } = Route.useSearch();

	const {
		data: tasksResponse,
		isLoading: tasksLoading,
		error: _getPartnerCompanyTasksError,
	} = useQuery({
		queryKey: ['partner-company-tasks', partnerId, { sort }],
		queryFn: () => getPartnerCompanyTasks(partnerId, constructQuery({ sort, page })),
	});
	const tasks = tasksResponse?.data;

	const updateQueryParams = useUpdateQueryParams();
	const handleSort = ([sort]) => {
		updateQueryParams({ sort: sortToString(sort) });
	};

	const handlePaginationModelChange = ({ page }: { page: number }) => {
		updateQueryParams({ page: page });
	};

	const columns: DataGridColumn<Task>[] = [
		{
			headerName: 'Description',
			field: 'name',
			flex: 1,
		},
		{
			field: 'createdAt',
			headerName: 'Assigned',
			width: 120,
			valueFormatter: (params) => new Date(params.value).toLocaleDateString('en-GB'),
		},
		{
			field: 'updatedAt',
			headerName: 'Last updated',
			width: 120,
			valueFormatter: (params) => new Date(params.value).toLocaleDateString('en-GB'),
		},
		{
			field: 'validTo',
			headerName: 'Valid to',
			width: 100,
			valueFormatter: (params) => params.value && new Date(params.value).toLocaleDateString('en-GB'),
		},
		{
			field: 'status',
			headerName: 'Status',
			renderCell: (cell) => <Badge variant={variants[cell.row.status]}>{states[cell.row.status]}</Badge>,
			sortable: false,
			width: 175,
		},
	];

	const handleRowClick = ({ row }) => {
		navigate({ to: `/partner/${row.groupId}/tasks/${row.id}` });
		return null;
	};

	if (tasksLoading) {
		return <Spinner className="text-[#1E1E1E]" />;
	}

	return (
		<>
			<div className="mt-4 px-4">
				<Paper className="mt-2" variant="outlined">
					<DataGrid
						data={tasks || []}
						columns={columns}
						paginationMode="server"
						sortingMode="server"
						onPaginationModelChange={handlePaginationModelChange}
						onSortModelChange={handleSort}
						sortingOrder={['asc', 'desc']}
						sortModel={[stringToSort(sort || 'updatedAt:desc')]}
						onRowClick={handleRowClick}
						pagination
						rowCount={tasksResponse?.count || 0}
						pageSize={tasksResponse?.pageSize}
						page={tasksResponse?.page}
						getRowClassName={(p) => p.row.markedForReview && 'error'}
						// onReload={revalidateTasks}
					/>
				</Paper>
			</div>
		</>
	);
}
